import { useFadeIn } from '@hooks/useFadeIn'
import { storyChapters, storyInsights } from '@content/storyInsights'
import type { BlogPost } from './Writing'
import postsData from '../../blog/posts.json'

export function StorySection({ onReadStory }: { onReadStory: (post: BlogPost) => void }) {
  const fade = useFadeIn()
  const storyPost = (postsData as BlogPost[]).find((p) => p.slug === 'from-clay-to-code')
  const philosophy = storyInsights.philosophy?.[0]
  const epiphany = storyInsights.epiphany?.[0]

  return (
    <section id="story" ref={fade.ref as React.RefObject<HTMLElement>} className={`modern-section ${fade.className}`}>
      <p className="modern-section__label">My Story</p>
      <h2 className="modern-section__title">From Clay to Code</h2>

      <div className="modern-story__body">
        <p className="modern-about__text">
          Before I ever wrote my first line of code, I spent nearly a decade at a
          farm-based Montessori school. The same instinct that made me good with kids,
          meeting people where they are, breaking complex things into understandable
          pieces, turned out to be exactly what engineering demands.
        </p>
        <p className="modern-about__text" style={{ marginTop: '1rem' }}>
          That curiosity carried me through payment processing, tax automation at
          scale, and enterprise security monitoring, until a layoff put me back at a
          blank page.
        </p>
        {epiphany && (
          <blockquote className="modern-story__pullquote">
            <p className="modern-story__pullquote-label">The turning point</p>
            <p className="modern-story__pullquote-quote">{epiphany.quote}</p>
            {epiphany.attribution && (
              <cite className="modern-story__pullquote-cite">{epiphany.attribution}</cite>
            )}
          </blockquote>
        )}
        <p className="modern-about__text" style={{ marginTop: '1rem' }}>
          That tool became Rubber Ducky, then a CLI called Responder, then can.code:
          80+ projects in two years blending voice-AI, accessibility, and developer
          tools. The thread connecting all of it: the most powerful use of technology
          isn't replacing human thinking. It's amplifying our natural problem-solving
          processes.
        </p>
        {philosophy && (
          <blockquote className="modern-about__insight">
            {philosophy.quote}
            {philosophy.attribution && (
              <cite className="modern-about__insight-cite">{philosophy.attribution}</cite>
            )}
          </blockquote>
        )}
      </div>

      <div className="modern-story__chapters">
        {storyChapters.map((ch, i) => (
          <div key={ch.label} className="modern-story__chapter">
            <span className="modern-story__chapter-label">{ch.label}</span>
            <span className="modern-story__chapter-period">{ch.period}</span>
            {i < storyChapters.length - 1 && (
              <span className="modern-story__chapter-arrow" aria-hidden="true">&rarr;</span>
            )}
          </div>
        ))}
      </div>

      {storyPost && (
        <button
          className="modern-btn modern-btn--secondary"
          style={{ marginTop: '2rem' }}
          onClick={() => onReadStory(storyPost)}
        >
          Read the full story &rarr;
        </button>
      )}
    </section>
  )
}
